import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { KeyRound } from "lucide-react";

export function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const reset = () => setForm({ current_password: "", new_password: "", confirm: "" });

  const submit = async () => {
    if (!form.current_password || !form.new_password) { toast.error("Fill in both password fields"); return; }
    if (form.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (form.new_password !== form.confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated");
      setOpen(false);
      reset();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not change password");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <button data-testid="open-change-password"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground transition-colors">
        <KeyRound size={16} /> Change password
      </button>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Change password</DialogTitle>
          <DialogDescription>Update the password you use to sign in. Minimum 8 characters.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Current password</Label>
            <Input type="password" value={form.current_password} data-testid="current-password"
              onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">New password</Label>
            <Input type="password" value={form.new_password} data-testid="new-password"
              onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Confirm new password</Label>
            <Input type="password" value={form.confirm} data-testid="confirm-password"
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-change-password"
            className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">{saving ? "Saving…" : "Update password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
